/** Settings → Contracts tab (Tom 2026-08-12: "i want all these fields to be a
 * default that is controllable from the settings in the admin section").
 *
 * Admin sets the FARBAR terms that repeat on every deal — buyer company, the
 * title/escrow block, standard deposit, inspection period, additional terms,
 * and the two cash-wholesale checkboxes — plus the typed-signature name/
 * initials and the blank-form/font uploads. These prefill the FARBAR modal on
 * every property; the deal-sourced fields (seller, address, county, parcel,
 * price, and the Zillow listing agent/broker) always come from the property, so
 * they aren't defaults. Writes go straight to the admin-gated FARBAR settings
 * endpoint (same one the modal's old inline setup used). */
import { useEffect, useRef, useState } from "react";
import {
  type FarbarValues, type FarbarStatus,
  farbarStatus, getFarbarDefaults, saveFarbarDefaults,
  uploadFarbarTemplate, uploadFarbarSignFont,
} from "./api";

// default-able term fields (everything that isn't pulled from the property)
const DEF_FIELDS: { key: keyof FarbarValues; label: string; wide?: boolean; area?: boolean; ph?: string }[] = [
  { key: "buyerCompany", label: "Buyer (your company)", wide: true, ph: "House Flips By Tom LLC" },
  { key: "initialDeposit", label: "Initial deposit $", ph: "1,000.00" },
  { key: "titleCompany", label: "Title / escrow company", wide: true },
  { key: "titleAddress", label: "Title company address", wide: true },
  { key: "titlePhone", label: "Title phone" },
  { key: "titleEmail", label: "Title email" },
  { key: "closingDate", label: "Closing date", ph: "usually per-deal — leave blank" },
  { key: "inspectionDays", label: "Inspection period (days)", ph: "10" },
  { key: "additionalTerms", label: "Additional terms", wide: true, area: true },
];

const CHECK_FIELDS: { key: keyof FarbarValues; label: string }[] = [
  { key: "cashSale", label: "Cash transaction — no financing contingency (¶8a)" },
  { key: "buyerClosingAgent", label: "Buyer designates Closing Agent & pays owner's title policy (¶9c-ii)" },
  { key: "mayAssign", label: "Buyer may assign, but not released from liability (¶7 assignability)" },
];

export function FarbarSettingsTab({ token, isAdmin }: { token: string | null; isAdmin: boolean }) {
  const [status, setStatus] = useState<FarbarStatus | null>(null);
  const [d, setD] = useState<FarbarValues>({});
  const [signName, setSignName] = useState("");
  const [signInitials, setSignInitials] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const tplRef = useRef<HTMLInputElement>(null);
  const fontRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const [st, def] = await Promise.all([farbarStatus(token), getFarbarDefaults(token)]);
      if (!alive) return;
      setStatus(st);
      setD(def.defaults ?? {});
      setSignName(def.signName ?? "");
      setSignInitials(def.signInitials ?? "");
    })();
    return () => { alive = false; };
  }, [token]);

  const set = (k: keyof FarbarValues, v: string) => setD((p) => ({ ...p, [k]: v }));
  // unset checkbox = default ON (matches the modal, which ticks both unless a
  // saved default says otherwise), so an admin who never touched them still
  // sees the cash-wholesale defaults reflected here.
  const checkOn = (k: keyof FarbarValues) => (d[k] === undefined ? true : d[k] === "1");

  const save = async () => {
    if (busy) return;
    setBusy(true); setNote(null);
    try {
      // write the two checkboxes explicitly so the stored default is unambiguous
      const defs: FarbarValues = {
        ...d,
        cashSale: checkOn("cashSale") ? "1" : "",
        buyerClosingAgent: checkOn("buyerClosingAgent") ? "1" : "",
        mayAssign: checkOn("mayAssign") ? "1" : "",
      };
      await saveFarbarDefaults(token, { defaults: defs, signName, signInitials });
      setNote("Saved ✓ — these prefill every new contract.");
    } catch (e) {
      setNote((e as { detail?: string; message?: string })?.detail ?? (e as Error)?.message ?? "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const doUpload = async (kind: "tpl" | "font", file: File) => {
    setBusy(true); setNote(null);
    try {
      if (kind === "tpl") {
        const r = await uploadFarbarTemplate(token, file);
        setNote(`Blank FARBAR saved — ${(r["fieldCount"] as number) ?? 0} fields, ${(r["pages"] as number) ?? "?"} pages ✓`);
      } else {
        await uploadFarbarSignFont(token, file);
        setNote("Signature font saved ✓");
      }
      setStatus(await farbarStatus(token));
    } catch (e) {
      setNote((e as { detail?: string; message?: string })?.detail ?? (e as Error)?.message ?? "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="setsec">
        <h3>Contract defaults</h3>
        <div className="setgate">Only admins can change the FARBAR contract defaults.</div>
      </div>
    );
  }

  return (
    <div className="setsec">
      <h3>Contract defaults (FARBAR)</h3>
      <p className="sub setnote">
        These prefill every FARBAR contract you generate from a property. The seller,
        property address, county, parcel, price and the Zillow listing agent/broker
        fill in automatically from each property — set everything else here once.
      </p>

      <div className="setroigroup">
        <div className="setroigrouphd">Blank form &amp; signature</div>
        <div className="fbsetrow">
          <span>Blank FARBAR PDF: <b>{status?.hasTemplate ? `loaded (${status.nFields} fields)` : "none uploaded"}</b></span>
          <button className="btn slim" disabled={busy} onClick={() => tplRef.current?.click()}>Upload PDF</button>
          <input ref={tplRef} type="file" accept="application/pdf" hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void doUpload("tpl", f); e.target.value = ""; }} />
        </div>
        <div className="fbsetrow">
          <span>Signature font: <b>{status?.hasSignFont ? "custom loaded" : "default (Times-Italic)"}</b></span>
          <button className="btn slim" disabled={busy} onClick={() => fontRef.current?.click()}>Upload .ttf</button>
          <input ref={fontRef} type="file" accept=".ttf,.otf,font/ttf,font/otf" hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void doUpload("font", f); e.target.value = ""; }} />
        </div>
        <div className="setgrid">
          <label className="setroifield">
            <span className="setroilabel">Typed signature name</span>
            <input className="setroiinput" value={signName} placeholder="Tom Zadok" onChange={(e) => setSignName(e.target.value)} />
          </label>
          <label className="setroifield">
            <span className="setroilabel">Initials</span>
            <input className="setroiinput" value={signInitials} placeholder="TZ" onChange={(e) => setSignInitials(e.target.value)} />
          </label>
        </div>
      </div>

      <div className="setroigroup">
        <div className="setroigrouphd">Standard terms</div>
        <div className="setgrid">
          {DEF_FIELDS.map((f) => (
            <label key={f.key} className={"setroifield" + (f.wide ? " wide" : "")}>
              <span className="setroilabel">{f.label}</span>
              {f.area ? (
                <textarea className="setroiinput" rows={2} value={d[f.key] ?? ""} placeholder={f.ph ?? ""} onChange={(e) => set(f.key, e.target.value)} />
              ) : (
                <input className="setroiinput" value={d[f.key] ?? ""} placeholder={f.ph ?? ""} onChange={(e) => set(f.key, e.target.value)} />
              )}
            </label>
          ))}
        </div>
      </div>

      <div className="setroigroup">
        <div className="setroigrouphd">Default checkboxes</div>
        <div className="fbchecks">
          {CHECK_FIELDS.map((c) => (
            <label key={c.key} className="fbcheck">
              <input type="checkbox" checked={checkOn(c.key)} onChange={(e) => set(c.key, e.target.checked ? "1" : "")} />
              <span>{c.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="setactions">
        <button className="btn primary" disabled={busy} onClick={() => void save()}>{busy ? "Saving…" : "Save contract defaults"}</button>
        {note ? <span className="sub on">{note}</span> : null}
      </div>
    </div>
  );
}
